import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MessageSquare, FileText, BookOpen, Plane, ArrowRight, Calendar, MapPin, GraduationCap, Clock, User } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import TestimonialCarousel from '../components/testimonial-carousel/TestimonialCarousel';
import settings from '../constants/settings';
import { loadBeasiswaPosts } from '../utils/beasiswa';
import { loadBlogPosts } from '../utils/blog';
import { loadTestimonialPosts } from '../utils/testimonial';

const Home = () => {
  const [recentBeasiswa, setRecentBeasiswa] = useState([]);
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBlogLoading, setIsBlogLoading] = useState(true);
  const [isTestimonialLoading, setIsTestimonialLoading] = useState(true);

  // Fetch recent beasiswa data
  useEffect(() => {
    const fetchBeasiswaData = async () => {
      try {
        const fetchedBeasiswa = await loadBeasiswaPosts();
        
        // Sort beasiswa by date (newest first) and take only the 3 most recent
        const sortedBeasiswa = fetchedBeasiswa
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 3);
        
        setRecentBeasiswa(sortedBeasiswa);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching beasiswa data:', error);
        setIsLoading(false);
      }
    };

    fetchBeasiswaData();
  }, []);

  // Fetch recent blog data
  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        const fetchedBlogs = await loadBlogPosts();
        
        // Sort blogs by date (newest first) and take only the 3 most recent
        const sortedBlogs = fetchedBlogs
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .slice(0, 3);
        
        setRecentBlogs(sortedBlogs);
        setIsBlogLoading(false);
      } catch (error) {
        console.error('Error fetching blog data:', error);
        setIsBlogLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  // Fetch testimonial data
  useEffect(() => {
    const fetchTestimonialData = async () => {
      try {
        const fetchedTestimonials = await loadTestimonialPosts();
        setTestimonials(fetchedTestimonials);
        setIsTestimonialLoading(false);
      } catch (error) {
        console.error('Error fetching testimonial data:', error);
        setIsTestimonialLoading(false);
      }
    };

    fetchTestimonialData();
  }, []);

  // Format date to Indonesian format
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('id-ID', options);
  };

  // Calculate days remaining until deadline
  const calculateDaysRemaining = (deadlineString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(deadlineString);
    deadline.setHours(0, 0, 0, 0);
    
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Service data
  const services = [
    {
      id: '/layanan/1',
      icon: <MessageSquare className="h-10 w-10 text-gold" />,
      title: 'Konsultasi Beasiswa GRATIS',
      description: 'Bebas konsul info terkini soal negara, kampus, dan beasiswa internasional yang jadi target kamu.'
    },
    {
      id: '/services?category=Dokumen',
      icon: <FileText className="h-10 w-10 text-gold" />,
      title: 'Persiapan Dokumen Standar Internasional',
      description: 'Kami bantu kamu siapin dokumen aplikasi yang profesional dan sesuai standar internasional.'
    },
    {
      id: '/services?category=Bimbingan%20Belajar',
      icon: <BookOpen className="h-10 w-10 text-gold" />,
      title: 'Bimbingan Tes Internasional',
      description: 'Program persiapan intensif untuk tes TOEFL, IELTS, Bahasa Arab, dan tes khusus beasiswa.'
    },
    {
      id: '/services?category=Trip%20Edukasi',
      icon: <Plane className="h-10 w-10 text-gold" />,
      title: 'Trip Edukasi ke Negara Tujuan',
      description: 'Rasakan pengalaman belajar langsung di negara tujuan melalui program campus tour, umroh plus mulazamah, dan lainnya.'
    }
  ];



  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>#PintarPilihKuliah | Support System untuk Kuliah di Luar Negeri</title>
        <meta name="description" content="Platform untuk memudahkan alumni pesantren di Indonesia dalam mengakses peluang pendidikan internasional. Konsultasi beasiswa, persiapan dokumen, dan bimbingan tes." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-[#FDF6F0] mt-[90px]">
        <div className="container mx-auto px-4 py-16 md:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-serif leading-tight mb-8 text-gray-900">
              Support System Untuk 
              <span className="mt-2 inline-block">Kuliah di Luar Negeri</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-16 font-light leading-relaxed max-w-3xl mx-auto">
              Mau update info beasiswa luar negeri? Komunitas <b>#PintarPilihKuliah</b> siap bantuin dan bimbing kamu untuk <b>Kuliah Go International</b>
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-serif text-gray-900 mb-1">11200+</div>
                <div className="text-sm text-gray-600">Member Komunitas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-serif text-gray-900 mb-1">22+</div>
                <div className="text-sm text-gray-600">Alumni Berhasil</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-serif text-gray-900 mb-1">10+</div>
                <div className="text-sm text-gray-600">Negara Tujuan</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-serif text-gray-900 mb-1">12+</div>
                <div className="text-sm text-gray-600">Layanan yang Merakyat</div>
              </div>
            </div>
            
            <p className="text-lg md:text-xl text-gray-600 mb-2 font-light mx-auto">
              Bismillah, ikhtiar aja dulu..
            </p>

              <Link
              to={settings.wa_group_link} 
                target="_blank" 
                rel="noopener noreferrer"
              className="px-8 py-4 bg-orange-500 hover:bg-black text-white font-medium rounded-md inline-flex items-center justify-center gap-3 transition-all text-lg shadow-lg hover:shadow-xl hover:translate-y-[-2px]"
              >
                <svg
                className="w-6 h-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              Join Grup WhatsApp #PintarPilihKuliah
            </Link>
          </div>
        </div>
      </section>

      {/* Beasiswa Section */}
      <section className="py-10 relative overflow-hidden bg-gradient-to-b from-[#FDF6F0] to-white">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 -left-32 w-64 h-64 rounded-full bg-gold/5"></div>
          <div className="absolute bottom-0 right-0 w-full h-1/3 bg-gradient-to-t from-white to-transparent"></div>
          <div className="absolute top-20 left-10 w-8 h-8 rounded-full bg-gold/20"></div>
          <div className="absolute top-40 right-20 w-4 h-4 rounded-full bg-gold/30"></div>
          <div className="absolute bottom-20 left-1/4 w-6 h-6 rounded-full bg-gold/20"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="bg-gold/10 text-gold px-4 py-1 rounded-full text-sm font-medium inline-block mb-3">Peluang Beasiswa Terbaru</span>    
          </div>

          {/* Scholarship Cards */}
          <div className="mb-16">
            {isLoading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
              </div>
            ) : recentBeasiswa.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {/* Featured Scholarship (First Card) */}
                <div className="md:col-span-6 lg:col-span-5">
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gold/20 hover:border-gold/70 h-full transform transition-all duration-300 hover:-translate-y-1 hover:shadow-xl group">
                    <Link to={`/beasiswa/${recentBeasiswa[0].slug}`} className="block relative">
                      <div className="aspect-[16/9] overflow-hidden bg-gray-100 relative">
                        <img
                          src={recentBeasiswa[0].coverImage}
                          alt={recentBeasiswa[0].title}
                          className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                        {!isNaN(calculateDaysRemaining(recentBeasiswa[0].deadline)) && (
                          calculateDaysRemaining(recentBeasiswa[0].deadline) > 0 ? (
                            <div className="absolute top-4 right-4 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                              {calculateDaysRemaining(recentBeasiswa[0].deadline)} hari lagi
                            </div>
                          ) : (
                            <div className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md">
                              Ditutup
                            </div>
                          )
                        )}
                        <div className="absolute bottom-4 left-4 bg-gold/90 text-white text-xs font-bold px-3 py-1 rounded-full">
                          FEATURED
                        </div>
                      </div>
                    </Link>
                    <div className="p-6">
                      <Link to={`/beasiswa/${recentBeasiswa[0].slug}`} className="block mb-3">
                        <h3 className="text-xl font-bold text-gray-900 line-clamp-2 group-hover:text-gold transition-colors">{recentBeasiswa[0].title}</h3>
                      </Link>
                      
                      <div className="flex flex-col gap-3 mb-4">
                        <div className="flex items-center text-gray-600">
                          <Calendar className="h-4 w-4 mr-2 text-gold" />
                          <span className="font-medium">Deadline: {recentBeasiswa[0].deadline ? (new Date(recentBeasiswa[0].deadline).toString() === 'Invalid Date' ? recentBeasiswa[0].deadline : formatDate(recentBeasiswa[0].deadline)) : '-'}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <MapPin className="h-4 w-4 mr-2 text-gold" />
                          <span className="font-medium">{recentBeasiswa[0].country}</span>
                        </div>
                        <div className="flex items-center text-gray-600">
                          <GraduationCap className="h-4 w-4 mr-2 text-gold" />
                          <span className="font-medium">{recentBeasiswa[0].degree}</span>
                        </div>
                      </div>
                      
                      <p className="text-gray-600 mb-5 line-clamp-3">{recentBeasiswa[0].excerpt}</p>
                      
                      <Link
                        to={`/beasiswa/${recentBeasiswa[0].slug}`}
                        className="inline-flex items-center justify-center w-full bg-gold hover:bg-dark-navy text-white font-medium rounded-lg px-4 py-2.5 transition-all duration-300 shadow-md hover:shadow-lg"
                      >
                        Lihat Detail <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Other Scholarships */}
                <div className="md:col-span-6 lg:col-span-7">
                  <div className="grid grid-cols-1 gap-6 h-full">
                    {recentBeasiswa.slice(1).map((beasiswa, index) => (
                      <div 
                        key={beasiswa.slug} 
                        className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:border-gold/50 transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group"
                      >
                        <div className="flex flex-col md:flex-row h-full">
                          <Link to={`/beasiswa/${beasiswa.slug}`} className="md:w-2/5 relative">
                            <div className="aspect-[16/9] md:aspect-auto md:h-full overflow-hidden bg-gray-100">
                              <img
                                src={beasiswa.coverImage}
                                alt={beasiswa.title}
                                className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
                                onError={(e) => {
                                  e.target.src = 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1471&q=80';
                                }}
                              />
                              {!isNaN(calculateDaysRemaining(beasiswa.deadline)) && (
                                calculateDaysRemaining(beasiswa.deadline) > 0 ? (
                                  <div className="absolute top-3 right-3 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                    {calculateDaysRemaining(beasiswa.deadline)} hari lagi
                                  </div>
                                ) : (
                                  <div className="absolute top-3 right-3 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                                    Ditutup
                                  </div>
                                )
                              )}
                            </div>
                          </Link>
                          <div className="p-5 md:w-3/5 flex flex-col justify-between">
                            <div>
                              <Link to={`/beasiswa/${beasiswa.slug}`} className="block mb-2">
                                <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-gold transition-colors">{beasiswa.title}</h3>
              </Link>
                              
                              <div className="flex flex-wrap gap-4 mb-3 text-sm">
                                {/* <div className="flex items-center text-gray-600">
                                  <Calendar className="h-4 w-4 mr-1 text-gold" />
                                  <span>{formatDate(beasiswa.deadline)}</span>
                                </div> */}
                                <div className="flex items-center text-gray-600">
                                  <MapPin className="h-4 w-4 mr-1 text-gold" />
                                  <span>{beasiswa.country}</span>
                                </div>
                                <div className="flex items-center text-gray-600">
                                  <GraduationCap className="h-4 w-4 mr-2 text-gold" />
                                  <span className="font-medium">{beasiswa.degree}</span>
                                </div>
                              </div>
                              
                              <p className="text-gray-600 mb-4 line-clamp-2 text-sm">{beasiswa.excerpt}</p>
                            </div>
                            
              <Link 
                              to={`/beasiswa/${beasiswa.slug}`}
                              className="inline-flex items-center justify-center bg-white border border-gold text-gold hover:bg-gold hover:text-white font-medium rounded-md px-3 py-1.5 text-sm transition-all"
              >
                              Baca Selengkapnya <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-20 bg-white/50 rounded-xl">
                <div className="text-gray-400 mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Tidak Ada Beasiswa Ditemukan</h3>
                <p className="text-gray-500">
                  Maaf, tidak ada data beasiswa tersedia saat ini.
                </p>
              </div>
            )}
          </div>

          <div className="text-center relative">
            {/* Decorative line elements */}
            <div className="absolute left-0 top-1/2 h-px w-1/4 bg-gradient-to-r from-transparent to-gray-300 hidden md:block"></div>
            <div className="absolute right-0 top-1/2 h-px w-1/4 bg-gradient-to-l from-transparent to-gray-300 hidden md:block"></div>
            
            <Link
              to="/beasiswa"
              className="inline-flex items-center justify-center px-6 py-3 bg-gold hover:bg-dark-navy text-white font-medium rounded-lg transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-1"
            >
              Lihat Semua Beasiswa <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-serif leading-tight mb-4 text-gray-900">Layanan Merakyat untuk Mewujudkan Kuliah Impian</h2>
            <p className="text-gray text-lg">
            Dirancang untuk semua golongan agar bisa melanjutkan studi di luar negeri
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <Card key={service.id} className="border-2 border-cream/30 hover:border-gold/50 transition-all duration-300 hover:shadow-lg">
                <CardHeader>
                  <div className="mb-4">{service.icon}</div>
                  <CardTitle className="text-gray-900 font-serif text-xl">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
                <CardFooter>
                  <Link 
                    to={`${service.id}`} 
                    className="inline-flex items-center text-gold hover:text-cream font-medium"
                  >
                    Selengkapnya <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-[#1a2639] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#b99764]"></div>
          <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-[#b99764]"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-serif leading-tight mb-4 text-white">Kisah Sukses Alumni #PintarPilihKuliah</h2>
            <p className="text-white/80 text-lg">
              Pengalaman mereka yang berhasil tembus kuliah di luar negeri dengan dukungan komunitas kami
            </p>
          </div>

          {isTestimonialLoading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
            </div>
          ) : testimonials.length > 0 ? (
            <TestimonialCarousel testimonials={testimonials.map(testimonial => ({
              id: testimonial.slug,
              name: testimonial.name,
              position: testimonial.position,
              excerpt: testimonial.excerpt,
              body: testimonial.content,
              year: testimonial.year,
              image: testimonial.image || null
            }))} />
          ) : (
            <div className="text-center py-20">
              <div className="text-white/40 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Tidak Ada Testimoni</h3>
              <p className="text-white/60">
                Maaf, tidak ada testimoni tersedia saat ini.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 bg-[#f8f8f8] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white to-transparent"></div>
        </div>
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="bg-gold/10 text-gold px-4 py-1 rounded-full text-sm font-medium inline-block mb-3">Tips & Inspirasi</span>
          </div>

          {isBlogLoading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
            </div>
          ) : recentBlogs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {recentBlogs.map((blog, index) => (
                <div 
                  key={blog.slug}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100"
                >
                  <Link to={`/blog/${blog.slug}`} className="block">
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img 
                        src={blog.coverImage} 
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80';
                        }}
                      />
                      <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/70 to-transparent h-1/2 pointer-events-none"></div>
                    </div>
                  </Link>
                  
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>{new Date(blog.date).toLocaleDateString('id-ID', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <User className="h-4 w-4 mr-1" />
                        <span>{blog.author}</span>
                      </div>
                    </div>
                    
                    <Link to={`/blog/${blog.slug}`} className="block mb-3">
                      <h3 className="text-xl font-bold text-gray-900 line-clamp-2 group-hover:text-gold transition-colors">
                        {blog.title}
                      </h3>
                    </Link>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {blog.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {blog.tags && blog.tags.map((tag, i) => (
                        <span key={i} className="bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs">
                          #{tag}
                        </span>
                      ))}
                    </div>
                    
                    <Link
                      to={`/blog/${blog.slug}`}
                      className="inline-flex items-center justify-center w-full bg-white border border-gold text-gold hover:bg-gold hover:text-white font-medium rounded-md py-2.5 transition-all duration-300"
                    >
                      Baca Selengkapnya <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              ))}
                </div>
          ) : (
            <div className="text-center py-20 bg-white/50 rounded-xl">
              <div className="text-gray-400 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Tidak Ada Artikel Ditemukan</h3>
              <p className="text-gray-500">
                Maaf, tidak ada artikel blog tersedia saat ini.
              </p>
            </div>
          )}

          <div className="text-center mt-16">
            <Link
              to="/blog"
              className="inline-flex items-center justify-center px-6 py-3 bg-gold hover:bg-dark-navy text-white font-medium rounded-lg transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-1"
            >
              Lihat Semua Artikel <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-[#FDF6F0] scroll-mt-32">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-serif leading-tight mb-4 text-gray-900">Pertanyaan yang Sering Diajukan</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
              <AccordionItem value="item-1" className="border-b border-gray-200">
                <AccordionTrigger className="px-6 py-4 text-left font-serif text-lg text-gray-900 hover:text-orange-500">
                  Apakah konsultasi beasiswa benar-benar gratis?
                </AccordionTrigger>
                <AccordionContent className="px-6 text-gray-600">
                  Ya, #PintarPilihKuliah menyediakan konsultasi beasiswa secara gratis untuk semua alumni pesantren dan pelajar se-Indonesia. Kami percaya bahwa informasi beasiswa adalah hak semua orang yang ingin melanjutkan pendidikan.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border-b border-gray-200">
                <AccordionTrigger className="px-6 py-4 text-left font-serif text-lg text-gray-900 hover:text-orange-500">
                  Berapa biaya untuk bergabung dengan komunitas #PintarPilihKuliah?
                </AccordionTrigger>
                <AccordionContent className="px-6 text-gray-600">
                  Komunitas #PintarPilihKuliah berada di grup WA. Kamu bisa join secara GRATIS. Dapatkan informasi terkini berbagai peluang kuliah di luar negeri beserta beasiswanya.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border-b border-gray-200">
                <AccordionTrigger className="px-6 py-4 text-left font-serif text-lg text-gray-900 hover:text-orange-500">
                  Apakah saya harus menggunakan semua layanan #PintarPilihKuliah?
                </AccordionTrigger>
                <AccordionContent className="px-6 text-gray-600">
                  Tidak, kamu boleh memilih layanan yang sesuai dengan kebutuhan kamu saja. Setiap layanan kami berdiri sendiri, meskipun akan lebih optimal jika digunakan secara terintegrasi dalam 1 paket pendampingan.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border-b border-gray-200">
                <AccordionTrigger className="px-6 py-4 text-left font-serif text-lg text-gray-900 hover:text-orange-500">
                  Bagaimana jika saya tidak memiliki background pesantren?
                </AccordionTrigger>
                <AccordionContent className="px-6 text-gray-600">
                  Meskipun fokus utama kami adalah alumni pesantren, kami juga menerima konsultasi dari siswa umum yang tertarik melanjutkan pendidikan ke luar negeri, terutama ke negara-negara Islam.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="border-b-0">
                <AccordionTrigger className="px-6 py-4 text-left font-serif text-lg text-gray-900 hover:text-orange-500">
                  Apakah ada jaminan diterima di universitas jika menggunakan layanan #PintarPilihKuliah?
                </AccordionTrigger>
                <AccordionContent className="px-6 text-gray-600">
                  #PintarPilihKuliah tidak memberikan jaminan penerimaan karena keputusan akhir ada di tangan universitas atau lembaga pemberi beasiswa. Namun, kami memberikan pendampingan optimal untuk meningkatkan peluang kamu bisa diterima.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#f6d39d] text-dark-navy">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-serif leading-tight mb-8">Siap Kuliah Go International?</h2>
            <p className="text-xl leading-relaxed mb-12">
              Hunting bareng 11200+ alumni pesantren dan pelajar yang sudah gabung.<br className="hidden md:block"/> Jangan sampai ketinggalan peluang-peluang beasiswa luar negeri.
            </p>
            <Link
              to={settings.wa_group_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-8 py-4 bg-white hover:bg-dark-navy text-dark-navy hover:text-light-cream rounded-full inline-flex items-center justify-center gap-3 transition-all hover:shadow-lg border-2 border-gold hover:border-light-cream text-lg"
            >
              <svg
                className="w-6 h-6 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              </svg>
              Gabung Grup #PintarPilihKuliah di WhatsApp
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
